import { useEffect, useRef, useState } from "react";
import { Mic, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { mmss } from "../../lib/chat";
import { useLang } from "../../lib/i18n";

const MAX = 60;
const TYPES = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg"];

function pickType() {
  if (typeof MediaRecorder === "undefined") return null;
  return TYPES.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
}

// Mic button + recording bar. onSend({path, duration}) receives the uploaded audio.
export default function AudioRecorder({ onSend, disabled, autoFocus, onActiveChange }) {
  const { t } = useLang();
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [secs, setSecs] = useState(0);
  const [hint, setHint] = useState(autoFocus ? t("rec.ready") : "");
  const rec = useRef(null);
  const timer = useRef(null);
  const startedAt = useRef(0);
  const cancelled = useRef(false);
  const touchX = useRef(null);
  const [dx, setDx] = useState(0);

  useEffect(() => {
    onActiveChange?.(state !== "idle");
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stopTracks(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopTracks = () => {
    clearInterval(timer.current);
    rec.current?.stream?.getTracks().forEach((tr) => tr.stop());
  };

  const start = async () => {
    const type = pickType();
    if (type === null || !navigator.mediaDevices?.getUserMedia) return setHint(t("rec.unsupported"));
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setHint(t("rec.denied"));
      return toast.error(t("rec.denied"));
    }
    const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      stopTracks();
      const duration = Math.min(MAX, (Date.now() - startedAt.current) / 1000);
      if (cancelled.current || duration < 0.5) return setState("idle");
      setState("uploading");
      try {
        const mime = (mr.mimeType || type || "audio/webm").split(";")[0];
        const blob = new Blob(chunks, { type: mime });
        const fd = new FormData();
        fd.append("file", new File([blob], `audio.${mime.includes("mp4") ? "m4a" : mime.split("/")[1]}`, { type: mime }));
        const { data } = await api.post("/uploads/audio", fd);
        await onSend({ path: data.path, duration: Math.round(duration * 10) / 10 });
      } catch {
        toast.error(t("rec.failed"));
      }
      setState("idle");
    };
    rec.current = mr;
    cancelled.current = false;
    startedAt.current = Date.now();
    mr.start(250);
    setSecs(0);
    setHint("");
    setState("recording");
    timer.current = setInterval(() => {
      const s = (Date.now() - startedAt.current) / 1000;
      setSecs(s);
      if (s >= MAX) { toast(t("rec.limit")); finish(); }
    }, 200);
  };

  const finish = () => rec.current?.state === "recording" && rec.current.stop();
  const cancel = () => { cancelled.current = true; finish(); setDx(0); };

  if (state === "idle")
    return (
      <div className="flex items-center gap-2">
        {hint && <p className="max-w-[12rem] text-[11px] leading-tight text-slate-500" data-testid="recorder-hint">{hint}</p>}
        <button type="button" onClick={start} disabled={disabled} aria-label={t("rec.start")} data-testid="chat-mic-btn"
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-md transition-transform active:scale-90 disabled:opacity-40 ${autoFocus && hint ? "ring-4 ring-brand/25 animate-pulse" : ""}`}>
          <Mic className="h-5 w-5" />
        </button>
      </div>
    );

  return (
    <div className="flex flex-1 items-center gap-2" data-no-swipe data-testid="recorder-bar"
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchMove={(e) => touchX.current !== null && setDx(Math.min(0, e.touches[0].clientX - touchX.current))}
      onTouchEnd={() => { if (dx < -80) cancel(); else setDx(0); touchX.current = null; }}>
      <button type="button" onClick={cancel} disabled={state === "uploading"} aria-label={t("rec.cancel")} data-testid="recorder-cancel-btn"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-alert/10 text-alert transition-transform active:scale-90">
        <X className="h-5 w-5" />
      </button>
      <div className="flex flex-1 items-center gap-2 overflow-hidden rounded-full bg-mist px-4 py-2.5" style={{ transform: `translateX(${dx}px)` }}>
        <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-alert" />
        <span className="font-semibold tabular-nums text-navy" data-testid="recorder-timer">{mmss(secs)}</span>
        <span className="truncate text-xs text-slate-500">{state === "uploading" ? t("rec.uploading") : `${t("rec.recording")} · ${t("rec.slide")}`}</span>
      </div>
      <button type="button" onClick={finish} disabled={state === "uploading"} aria-label={t("msg.send")} data-testid="recorder-send-btn"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-md transition-transform active:scale-90 disabled:opacity-50">
        <Send className="h-5 w-5" />
      </button>
    </div>
  );
}
